import { decamelizeKeys } from "humps";
import cloneDeep from "lodash/cloneDeep";
import mapValues from "lodash/mapValues";
import omit from "lodash/omit";
import {
  FiltersV2Button,
  FiltersV2Panel,
  FiltersV2Preview,
  FiltersV2StoreContextProvider,
  useApiFilters,
  useFiltersV2MetaAutoUpdate,
  useOnFilterUpdate
} from "PFApp/components/filters_v2";
import { FiltersV2StorePage } from "PFApp/components/filters_v2/stores/filters_v2_store/filters_v2_store.types";
import SortBy from "PFApp/components/sort_by";
import { KEYWORD_FILTER_KEY } from "PFApp/search/tabs_constants";
import { Inline } from "PFComponents/containers";
import { isValueEmpty } from "PFCore/helpers/filters";
import { Meta } from "PFTypes";
import { FilterKeyV2, FiltersV2, FilterV2, KnownFiltersV2 } from "PFTypes/filters_v2";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import css from "./search_filters.module.scss";

type SearchFiltersContentProps = {
  pending?: boolean;
  meta: Meta;
  applyRequestFilters: (payload: Record<string, unknown>) => void;
  applyOrder: (payload: Record<string, unknown>) => void;
  omitInSidePanel?: FilterKeyV2[];
};

const SearchFiltersContent = ({
  pending,
  meta,
  applyRequestFilters,
  applyOrder,
  omitInSidePanel
}: SearchFiltersContentProps) => {
  const { t } = useTranslation("search");
  useOnFilterUpdate(KnownFiltersV2.Availability, ({ value }) => {
    if (isValueEmpty(value)) {
      applyOrder({});
    }
  });

  useFiltersV2MetaAutoUpdate(meta);

  const { apiFilters } = useApiFilters({ sendDefaultsAsUndefined: false, sendEmptyAsUndefined: false });

  useEffect(() => {
    if (apiFilters) {
      applyRequestFilters(decamelizeKeys(apiFilters) as Record<string, unknown>);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFilters]);

  return (
    <Inline
      justifyContent="space-between"
      reverse
      className={css.filtersWrap}
      role="region"
      aria-label={t("filters")}
      qaId="SearchPage.filters"
    >
      <Inline center>
        {meta.order && (
          <SortBy
            sort={meta.order.value?.text}
            options={meta.order.options.map((option) => ({
              id: option.text,
              displayElement: option.text,
              item: option
            }))}
            onSortChange={(value) => {
              applyOrder(value);
            }}
          />
        )}
        <FiltersV2Button withLabel disabled={pending} />
      </Inline>
      <FiltersV2Preview omit={[KnownFiltersV2.TemplateKey, KEYWORD_FILTER_KEY]} />
      <FiltersV2Panel
        omit={{ main: omitInSidePanel }}
        savedFiltersProps={{ omit: { main: [KnownFiltersV2.TemplateKey] } }}
      />
    </Inline>
  );
};

type SearchFiltersProps = Omit<SearchFiltersContentProps, "meta"> & {
  meta?: Meta;
  omitCompletely?: string[];
};

export const SearchFilters = ({ meta, omitCompletely = [], ...props }: SearchFiltersProps) => {
  const metaWithoutOmittedFilters = useMemo(() => {
    if (!meta?.filtersV2) {
      return null;
    }

    const metaDraft = cloneDeep(meta);

    metaDraft.filtersV2!.mainFilters = omit(metaDraft.filtersV2!.mainFilters, [
      KnownFiltersV2.Term, // on the search page, term filter is handled globally
      ...omitCompletely
    ]) as FiltersV2<FilterV2>;

    // on search default filters should not be required, search has to be able to find any data entry
    metaDraft.filtersV2!.mainFilters = mapValues(metaDraft.filtersV2!.mainFilters, (filter) => ({
      ...filter,
      required: false
    }));

    return metaDraft;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, ...omitCompletely]); // since omitCompletely does not have to be memoized, we should check it's content

  if (!metaWithoutOmittedFilters) {
    return null;
  }

  return (
    <FiltersV2StoreContextProvider name={FiltersV2StorePage.Search} persistent={false}>
      <SearchFiltersContent {...props} meta={metaWithoutOmittedFilters} />
    </FiltersV2StoreContextProvider>
  );
};
